import { showDeleteConfirmation } from '@modules/common';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { SupportCategoryResponse, SupportCategoryId } from '@business/entities/SupportCategories';
import { CategoryAPI } from '@business/api/SupportCategories';
import { handleMessage, requestMessage } from '@business/messages';
import { switchMap, map } from 'rxjs/operators';
import { R } from '@core/utils/r';

export class SupportCategoriesBloc extends BaseBloc {
  private readonly categoriesRepo = new Repository<SupportCategoryResponse>({
    getItemId: category => category.id,
  });

  readonly categories$ = this.categoriesRepo.items$.pipe(map(categories => R.sortBy(categories, c => c.title)));
  readonly selectCategory = this.categoriesRepo.selectItem;

  onReset() {
    this.categoriesRepo.reset();
  }

  fetchCategories = () => {
    return CategoryAPI.fetchCategories().pipe(
      this.categoriesRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_support_category_error') }),
    );
  };

  createCategory = (category: Partial<SupportCategoryResponse>) => {
    return CategoryAPI.createCategory(category).pipe(
      this.categoriesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_support_category'),
      }),
    );
  };

  updateCategory = (id: SupportCategoryId, category: Partial<SupportCategoryResponse>) => {
    return CategoryAPI.updateCategory(id, category).pipe(
      this.categoriesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_support_category'),
      }),
    );
  };

  deleteCategory = (categoryId: SupportCategoryId) => {
    return showDeleteConfirmation('Delete Category', 'Do you really want to remove this category').pipe(
      switchMap(() => CategoryAPI.deleteCategory(categoryId)),
      this.categoriesRepo.ops.removeOne(() => categoryId),
      handleMessage({
        type: requestMessage('delete_support_category'),
      }),
    );
  };
}
